import { useQuery } from "@tanstack/react-query";
import { usePublicClient } from "wagmi";
import { STAKE_ABI } from "@/contract/abi/stake";
import ContractAddress from "@/contract/addresses";
import type { PlanLimits } from "@/lib/stake/plan-dnm-limits";

/**
 * Reads per-plan DNM limits from contract via multicall (once per session).
 * Falls back to PLAN_DNM_LIMITS_FALLBACK_WEI while loading.
 *
 * Tuple literal keeps viem's multicall return type narrowed to
 * `readonly [bigint, bigint, bigint, bigint, bigint]` — matches `PlanLimits`
 * exactly, so no casting is required.
 */
export function usePlanDnmLimits(): {
  data: PlanLimits | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const publicClient = usePublicClient();

  const { data, isLoading, isError } = useQuery<PlanLimits>({
    queryKey: ["stake", "plan-dnm-limits"],
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !!publicClient,
    queryFn: async (): Promise<PlanLimits> => {
      if (!publicClient) throw new Error("No public client");

      const reads = await publicClient.multicall({
        contracts: [
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "allowedDnmAmountStake",
            args: [1],
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "allowedDnmAmountStake",
            args: [2],
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "allowedDnmAmountStake",
            args: [3],
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "allowedDnmAmountStake",
            args: [4],
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "allowedDnmAmountStake",
            args: [5],
          },
        ],
        allowFailure: false,
      });

      return [reads[0], reads[1], reads[2], reads[3], reads[4]];
    },
  });

  return { data, isLoading, isError };
}
